import { useWorkspace } from "@/hooks/use-workspace";
import { Grid3X3, Users, FileText, FileSignature, Calendar, CalendarDays, Settings, LogOut, ChevronDown, ChevronRight, BarChart3, Mail, CheckCircle2, XCircle, LayoutDashboard, ClipboardCheck, Shield, User, Mic2, ListMusic, DollarSign, Megaphone, Newspaper, Send, Eye, Camera, Receipt, HelpCircle, ArrowLeftRight, Handshake } from "lucide-react";
import { useSponsorHubAccess } from "@/hooks/use-sponsor-hub-access";
import { Link, useLocation } from "wouter";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useProfilePictureUpload } from "@/hooks/use-profile-picture-upload";
import { SquareCropDialog } from "@/components/square-crop-dialog";
import { useAuth } from "@/hooks/use-auth";
import { useEffectivePermissions } from "@/hooks/use-effective-permissions";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { SIDEBAR_NAV_ITEMS, ADMIN_NAV_ITEMS, BETA_NAV_ITEMS, TALENT_NAV_ITEMS, MARKETING_NAV_ITEMS, ENTERPRISE_PLUS_NAV_ITEMS, type NavItem } from "@shared/permissions/navigation";
import { useAgencyDashboardAccess } from "@/hooks/use-agency-dashboard-access";
import { Building2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { TutorialHint } from "@/components/tutorial-hint";
import { GlobalQuickFind } from "@/components/global-quick-find";
import { HelpChatWidget } from "@/components/help-chat-widget";
import { PlanBadge } from "@/components/plan-badge";
import { useState } from "react";
import { getAvatarSource, getAvatarInitials } from "@/lib/avatar";
import type { UserProfile } from "@shared/schema";

// Map sidebar nav item ids to their tutorial hint id (registry key).
// Items missing from this map render no hint. The map is shared across
// every sidebar surface (main, artist, marketing, admin, beta) so any
// nav item added in the future automatically picks up its hint.
const SIDEBAR_HINT_IDS: Record<string, string> = {
  leads: "sidebar.leads",
  clients: "sidebar.clients",
  forms: "sidebar.forms",
  invoices: "sidebar.invoices",
  // Master Schedule and Email Hub currently live in TOP_NAV_ITEMS, but
  // the entries are kept here too so any future sidebar surface that
  // renders them picks up the hint automatically.
  schedule: "sidebar.masterSchedule",
  communications: "sidebar.emailHub",
};

function useCollapsibleGroup(key: string, defaultOpen = true) {
  const storageKey = `sidebar-group:${key}`;
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return defaultOpen;
    const v = window.localStorage.getItem(storageKey);
    return v === null ? defaultOpen : v === "1";
  });
  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try { window.localStorage.setItem(storageKey, next ? "1" : "0"); } catch {}
      return next;
    });
  };
  return { open, toggle };
}

function CollapsibleGroupLabel({ label, open, onToggle }: { label: string; open: boolean; onToggle: () => void }) {
  return (
    <SidebarGroupLabel
      asChild
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-1 cursor-pointer hover:text-foreground"
        data-testid={`button-toggle-group-${label.toLowerCase()}`}
        aria-expanded={open}
      >
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <span>{label}</span>
      </button>
    </SidebarGroupLabel>
  );
}

function SidebarLogoIcon({ sidebarIconPath }: { sidebarIconPath: string | null }) {
  const [imgFailed, setImgFailed] = useState(false);
  const showCustom = !!sidebarIconPath && !imgFailed;

  if (showCustom) {
    return (
      <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted flex items-center justify-center flex-shrink-0">
        <img
          src={sidebarIconPath!}
          alt="Company logo"
          className="w-full h-full object-contain"
          data-testid="img-sidebar-icon"
          onError={() => {
            console.warn(
              "[Sidebar] Custom sidebar icon failed to load from",
              sidebarIconPath,
              "— falling back to the Clarik mark. The asset may be streaming with a wrong/missing Content-Type."
            );
            setImgFailed(true);
          }}
        />
      </div>
    );
  }

  return (
    <div
      className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center flex-shrink-0"
      data-testid="img-sidebar-clarik-fallback"
    >
      <svg viewBox="0 0 64 64" className="w-6 h-6 text-primary-foreground" aria-hidden>
        <path
          d="M 41 22 A 12 12 0 1 0 41 42"
          fill="none"
          stroke="currentColor"
          strokeWidth="6"
          strokeLinecap="round"
        />
        <circle cx="48" cy="20" r="4" fill="currentColor" />
      </svg>
    </div>
  );
}

interface AppSidebarProps {
  viewAsUserId?: string | null;
  onSwitchBack?: () => void;
}

export function AppSidebar({ viewAsUserId, onSwitchBack }: AppSidebarProps) {
  const [location] = useLocation();
  const [helpOpen, setHelpOpen] = useState(false);
  const { user } = useAuth();
  const { open: navOpen, toggle: toggleNav } = useCollapsibleGroup("navigation");
  const { open: talentOpen, toggle: toggleTalent } = useCollapsibleGroup("talent");
  const { open: marketingOpen, toggle: toggleMarketing } = useCollapsibleGroup("marketing");
  const { open: enterprisePlusOpen, toggle: toggleEnterprisePlus } = useCollapsibleGroup("enterprisePlus");
  const { open: betaOpen, toggle: toggleBeta } = useCollapsibleGroup("beta");
  const { open: adminOpen, toggle: toggleAdmin } = useCollapsibleGroup("administration");

  const { permissions, role, isLoading: permissionsLoading } = useEffectivePermissions({
    viewAsUserId,
  });

  const { data: gmailStatus } = useQuery<{
    connected: boolean;
    email: string | null;
    valid?: boolean;
    userTokensConnected?: boolean;
    userTokensEmail?: string | null;
  }>({
    queryKey: ["/api/gmail/status"],
    refetchInterval: 60000, // Check every minute
    staleTime: 30000, // Consider data stale after 30 seconds
  });

  // Mirror the Settings page: the sidebar badge reflects the user's *personal*
  // Gmail connection. The workspace connector is a fallback that sends from a
  // different address, so showing "connected" when only the connector is up
  // is misleading from the user's point of view.
  const isGmailConnected = gmailStatus?.userTokensConnected === true;

  const { workspaceSlug } = useWorkspace();

  const { data: calendarStatus } = useQuery<{ connected: boolean; email: string | null }>({
    queryKey: ["/api/user/calendar/status"],
    queryFn: async () => {
      const res = await fetch(`/api/user/calendar/status`, { credentials: "include" });
      if (!res.ok) return { connected: false, email: null };
      return res.json();
    },
    enabled: !!workspaceSlug && workspaceSlug !== "__no_workspace__",
    refetchInterval: 60000,
    staleTime: 30000,
  });
  const { data: branding } = useQuery<{
    faviconPath: string | null;
    sidebarIconPath: string | null;
    workspaceName: string | null;
    customEmailFromName?: string | null;
  }>({
    queryKey: ["/api/branding/public", workspaceSlug],
    queryFn: async () => {
      const slug = workspaceSlug && workspaceSlug !== "__no_workspace__" ? workspaceSlug : null;
      const url = slug
        ? `/api/branding/public?workspaceSlug=${encodeURIComponent(slug)}`
        : `/api/branding/public`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
  });

  // Sidebar header reads the active workspace's brand
  // (customEmailFromName -> workspace.name) with Clarik as the
  // last-resort fallback so a freshly-bootstrapped workspace whose
  // admin hasn't yet picked a name doesn't surface another agency's
  // brand in its own UI.
  const sidebarBrandName =
    branding?.customEmailFromName?.trim() ||
    branding?.workspaceName?.trim() ||
    "Clarik";

  // Check if current user is linked to an talent (for Artist section visibility)
  const { data: linkedTalent } = useQuery<{ id: string; name: string } | null>({
    queryKey: ["/api/talent/by-user"],
  });

  const { data: profile } = useQuery<UserProfile>({
    queryKey: ["/api/profile"],
  });

  // Task #12 — when the admin is impersonating, swap the sidebar's
  // bottom-left identity (name / email / avatar fallback) to the
  // impersonated user. The avatar image already follows automatically
  // because `/api/profiles/me` is impersonation-aware on the server.
  const { data: viewingAsUserData } = useQuery<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  }>({
    queryKey: ["/api/workspace/user", viewAsUserId],
    queryFn: async () => {
      const res = await fetch(`/api/workspace/user/${viewAsUserId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch user");
      return res.json();
    },
    enabled: !!viewAsUserId,
  });
  const displayUser = viewAsUserId && viewingAsUserData
    ? viewingAsUserData
    : (user as { firstName?: string; lastName?: string; email?: string } | undefined);

  // Approvals inbox count — drives the badge on the Manager Panel
  // admin nav item (badgeKey: "approvalsInboxCount"). Refetches every
  // 60s so managers see new pending requests without a full reload.
  const { data: approvalsInbox } = useQuery<{ count: number }>({
    queryKey: ["/api/approvals/inbox-count"],
    refetchInterval: 60000,
    staleTime: 30000,
  });
  const approvalsInboxCount = approvalsInbox?.count ?? 0;

  const { data: meProfile } = useQuery<{ profile: { profilePicture: string | null } | null }>({
    queryKey: ["/api/profiles/me"],
  });
  const sidebarPhotoUrl = getAvatarSource({
    profilePicture: meProfile?.profile?.profilePicture,
    profileImageUrl: user?.profileImageUrl,
  });
  const {
    pickFile: pickSidebarPhoto,
    fileInputProps: sidebarPhotoInputProps,
    selectedFile: sidebarSelectedFile,
    cropOpen: sidebarCropOpen,
    cancelCrop: cancelSidebarCrop,
    confirmCrop: confirmSidebarCrop,
    isUploading: sidebarPhotoUploading,
    isRemoving: sidebarPhotoRemoving,
  } = useProfilePictureUpload();

  const { toast } = useToast();
  const uiLensMutation = useMutation({
    mutationFn: async (uiLens: "admin" | "artist") =>
      apiRequest("PATCH", "/api/profile/ui-lens", { uiLens }),
    onSuccess: (_, uiLens) => {
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
      toast({
        title: uiLens === "artist" ? "Switched to Talent view" : "Switched to Admin view",
      });
    },
    onError: (e: any) =>
      toast({ title: "Could not switch view", description: e?.message || "", variant: "destructive" }),
  });

  // Task #102 — Hide entertainment-only modules on the Clarik (saas)
  // workspace. workspaceType comes from /api/auth/user.
  const workspaceType = ((user as any)?.workspaceType as "tenant" | "saas" | undefined) ?? "tenant";
  const navFilter = (item: NavItem) => {
    const permOk = item.permissionKey === "always_visible" || permissions[item.permissionKey] === true;
    const hideMatch = item.hideOnWorkspaceType?.includes(workspaceType) ?? false;
    const showOk = !item.showOnlyOnWorkspaceType || item.showOnlyOnWorkspaceType.includes(workspaceType);
    return permOk && !hideMatch && showOk;
  };

  const sidebarNavItems = SIDEBAR_NAV_ITEMS.filter(navFilter);
  // Task #419 — Talent module group. The whole group is gated on
  // `talent.module.visible` (OFF by default for every role except admin);
  // each item is additionally gated on its own permission key via navFilter.
  const talentModuleEnabled = permissions["talent.module.visible"] === true;
  const talentNavItems = talentModuleEnabled ? TALENT_NAV_ITEMS.filter(navFilter) : [];
  const adminNavItems = ADMIN_NAV_ITEMS.filter(navFilter);

  // Task #586 — Agency Dashboard nav entry. Probes the server gate so the
  // link only renders when (flag + multi-talent + admin) all pass.
  const agencyAccess = useAgencyDashboardAccess();
  const agencyAccessEnabled = agencyAccess.data?.enabled === true;

  const sponsorHubAccess = useSponsorHubAccess();
  const sponsorHubEnabled = sponsorHubAccess.data?.enabled === true;
  const marketingNavItems = MARKETING_NAV_ITEMS.filter(navFilter).filter(
    (item) => item.id !== "sponsorHub" || sponsorHubEnabled
  );

  // Enterprise+ — testing-ground category, gated on the enterprise_plus tier
  // entitlement (`enterprisePlus.module.visible`, injected server-side). Each
  // item is additionally gated on its own permission key via navFilter.
  const enterprisePlusEnabled = permissions["enterprisePlus.module.visible"] === true;
  const enterprisePlusNavItems = enterprisePlusEnabled
    ? ENTERPRISE_PLUS_NAV_ITEMS.filter(navFilter)
    : [];

  const getRoleLabel = () => {
    switch (role) {
      case "sales_rep":
        return "Sales Rep";
      case "artist":
        return "Talent";
      case "billing":
        return "Billing";
      case "marketing":
        return "Marketing";
      case "admin":
        return "Admin";
      default:
        return "User";
    }
  };

  const getIconComponent = (item: NavItem) => {
    const iconMap: Record<string, typeof LayoutDashboard> = {
      dashboard: LayoutDashboard,
      leads: Grid3X3,
      clients: Users,
      forms: FileSignature,
      invoices: FileText,
      billing: DollarSign,
      adminPanel: Shield,
      myProfile: Mic2,
      myEvents: ListMusic,
      sponsorHub: Handshake,
    };
    return iconMap[item.id] || item.icon || LayoutDashboard;
  };

  return (
    <Sidebar>
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-3">
          <SidebarLogoIcon sidebarIconPath={branding?.sidebarIconPath ?? null} />
          <div className="flex flex-col min-w-0">
            <span className="font-semibold text-sm truncate" data-testid="text-sidebar-workspace-name">{sidebarBrandName}</span>
            <span className="text-xs text-muted-foreground truncate">
              {displayUser?.firstName || displayUser?.lastName
                ? [displayUser.firstName, displayUser.lastName].filter(Boolean).join(" ")
                : displayUser?.email || ""}
              {profile?.title ? `, ${profile.title}` : ""}
            </span>
            <div className="mt-1.5">
              <PlanBadge isAdmin={role === "admin"} />
            </div>
          </div>
        </div>
      </SidebarHeader>
      
      <div className="px-4 pt-2 pb-2">
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5" title={isGmailConnected ? `Connected: ${gmailStatus?.email}` : "Not connected"}>
            <Mail className="w-3.5 h-3.5 text-muted-foreground" />
            {isGmailConnected ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
            ) : (
              <XCircle className="w-3.5 h-3.5 text-muted-foreground/50" />
            )}
            <span className="text-muted-foreground">Gmail</span>
          </div>
          <div className="flex items-center gap-1.5" title={calendarStatus?.connected ? `Connected: ${calendarStatus.email}` : "Not connected"}>
            <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
            {calendarStatus?.connected ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />
            ) : (
              <XCircle className="w-3.5 h-3.5 text-muted-foreground/50" />
            )}
            <span className="text-muted-foreground">Calendar</span>
          </div>
        </div>
      </div>

      <div className="px-4 pt-1 pb-2 border-b border-border">
        <GlobalQuickFind />
      </div>
      
      <SidebarContent>
        <SidebarGroup>
          <CollapsibleGroupLabel label="Navigation" open={navOpen} onToggle={toggleNav} />
          {navOpen && (
          <SidebarGroupContent>
            {permissionsLoading ? (
              <div className="space-y-2 px-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : (
              <SidebarMenu>
                {sidebarNavItems.map((item) => {
                  const Icon = getIconComponent(item);
                  const hintId = SIDEBAR_HINT_IDS[item.id];
                  return (
                    <SidebarMenuItem key={item.id}>
                      <div className="flex items-center w-full">
                        <SidebarMenuButton asChild isActive={location === item.url} className="flex-1">
                          <Link href={item.url} data-testid={`nav-${item.id}`}>
                            <Icon className="w-4 h-4" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                        {hintId && (
                          <div className="px-2">
                            <TutorialHint id={hintId} />
                          </div>
                        )}
                      </div>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            )}
          </SidebarGroupContent>
          )}
        </SidebarGroup>
        
        {talentNavItems.length > 0 && (
          <SidebarGroup>
            <CollapsibleGroupLabel label="Talent" open={talentOpen} onToggle={toggleTalent} />
            {talentOpen && (
            <SidebarGroupContent>
              <SidebarMenu>
                {talentNavItems.map((item) => {
                  const Icon = getIconComponent(item);
                  const hintId = SIDEBAR_HINT_IDS[item.id];
                  return (
                    <SidebarMenuItem key={item.id}>
                      <div className="flex items-center w-full">
                        <SidebarMenuButton asChild isActive={location === item.url} className="flex-1">
                          <Link href={item.url} data-testid={`nav-${item.id}`}>
                            <Icon className="w-4 h-4" />
                            <span>{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                        {hintId && (
                          <div className="px-2">
                            <TutorialHint id={hintId} />
                          </div>
                        )}
                      </div>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
            )}
          </SidebarGroup>
        )}
        
        {marketingNavItems.length > 0 && (
          <SidebarGroup>
            <CollapsibleGroupLabel label="Marketing" open={marketingOpen} onToggle={toggleMarketing} />
            {marketingOpen && (
            <SidebarGroupContent>
              <SidebarMenu>
                {marketingNavItems.map((item) => {
                  const Icon = getIconComponent(item);
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton asChild isActive={location === item.url}>
                        <Link href={item.url} data-testid={`nav-${item.id}`}>
                          <Icon className="w-4 h-4" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
            )}
          </SidebarGroup>
        )}

        {enterprisePlusNavItems.length > 0 && (
          <SidebarGroup>
            <CollapsibleGroupLabel label="Enterprise+" open={enterprisePlusOpen} onToggle={toggleEnterprisePlus} />
            {enterprisePlusOpen && (
            <SidebarGroupContent>
              <SidebarMenu>
                {enterprisePlusNavItems.map((item) => {
                  const Icon = getIconComponent(item);
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton asChild isActive={location === item.url}>
                        <Link href={item.url} data-testid={`nav-${item.id}`}>
                          <Icon className="w-4 h-4" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
            )}
          </SidebarGroup>
        )}

        <SidebarGroup>
          <CollapsibleGroupLabel label="BetaTest" open={betaOpen} onToggle={toggleBeta} />
          {betaOpen && (
          <SidebarGroupContent>
            <SidebarMenu>
              {BETA_NAV_ITEMS.map((item) => {
                const Icon = getIconComponent(item);
                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton asChild isActive={location === item.url}>
                      <Link href={item.url} data-testid={`nav-${item.id}`}>
                        <Icon className="w-4 h-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
          )}
        </SidebarGroup>

        {(adminNavItems.length > 0 || agencyAccessEnabled) && (
          <SidebarGroup>
            <CollapsibleGroupLabel label="Administration" open={adminOpen} onToggle={toggleAdmin} />
            {adminOpen && (
            <SidebarGroupContent>
              <SidebarMenu>
                {adminNavItems.map((item) => {
                  const Icon = getIconComponent(item);
                  const badgeCount =
                    item.badgeKey === "approvalsInboxCount"
                      ? approvalsInboxCount
                      : 0;
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton asChild isActive={location === item.url}>
                        <Link href={item.url} data-testid={`nav-${item.id}`}>
                          <Icon className="w-4 h-4" />
                          <span>{item.title}</span>
                          {badgeCount > 0 && (
                            <span
                              className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-primary-foreground"
                              data-testid={`badge-${item.id}-count`}
                            >
                              {badgeCount}
                            </span>
                          )}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
                {/* Task #586 — Agency Dashboard nav entry. Only renders
                    when the server gate (flag + multi-talent + admin)
                    passes; with the flag off no link appears anywhere. */}
                {agencyAccessEnabled && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={location === "/agency"}>
                      <Link href="/agency" data-testid="nav-agencyDashboard">
                        <Building2 className="w-4 h-4" />
                        <span>Agency Dashboard</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
            )}
          </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarFooter className="p-4">
        <input {...sidebarPhotoInputProps} data-testid="input-sidebar-photo" />
        <div className="flex items-center gap-3 w-full p-2 rounded-lg hover-elevate">
          <button
            type="button"
            onClick={pickSidebarPhoto}
            disabled={sidebarPhotoUploading || sidebarPhotoRemoving}
            className="relative group rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Change profile photo"
            data-testid="button-sidebar-change-photo"
          >
            <Avatar className="h-8 w-8">
              <AvatarImage src={sidebarPhotoUrl} />
              <AvatarFallback>
                {getAvatarInitials(
                  displayUser?.firstName && displayUser?.lastName
                    ? `${displayUser.firstName} ${displayUser.lastName}`
                    : displayUser?.firstName || displayUser?.email || "User",
                )}
              </AvatarFallback>
            </Avatar>
            <span
              className="absolute inset-0 z-10 flex items-center justify-center rounded-full bg-black/50 text-white opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity pointer-events-none"
              aria-hidden="true"
            >
              <Camera className="w-3.5 h-3.5" />
            </span>
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex flex-1 items-center gap-2 text-left rounded-md hover-elevate -mx-1 px-1 py-1"
                data-testid="button-user-menu"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {displayUser?.firstName && displayUser?.lastName
                      ? `${displayUser.firstName} ${displayUser.lastName}`
                      : displayUser?.email || "User"}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{displayUser?.email}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem asChild>
                <Link href="/settings" className="cursor-pointer" data-testid="nav-settings">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </Link>
              </DropdownMenuItem>
              {profile?.role === "admin" && (
                <DropdownMenuItem asChild>
                  <Link href="/account/billing" className="cursor-pointer" data-testid="nav-account-billing">
                    <Receipt className="w-4 h-4 mr-2" />
                    Account Billing
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => setHelpOpen(true)}
                className="cursor-pointer"
                data-testid="button-open-help-chat"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Help
              </DropdownMenuItem>
              {(linkedTalent || (profile?.wearsHats || []).includes("artist")) && (
                <DropdownMenuItem
                  onClick={() => uiLensMutation.mutate(profile?.uiLens === "artist" ? "admin" : "artist")}
                  className="cursor-pointer"
                  data-testid="button-toggle-ui-lens"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {profile?.uiLens === "artist"
                    ? "View as Admin"
                    : `View as Talent${linkedTalent ? ` (${linkedTalent.name})` : ""}`}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => {
                  window.location.href = "/api/logout";
                }}
                className="cursor-pointer text-destructive focus:text-destructive"
                data-testid="button-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {viewAsUserId && onSwitchBack && (
          <button
            type="button"
            onClick={onSwitchBack}
            className="flex items-center gap-1.5 w-full px-2 py-1.5 rounded-md text-xs text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/30 transition-colors"
            data-testid="button-switch-back"
          >
            <ArrowLeftRight className="w-3.5 h-3.5 shrink-0" />
            Switch back to my account
          </button>
        )}
        <SquareCropDialog
          open={sidebarCropOpen}
          file={sidebarSelectedFile}
          onCancel={cancelSidebarCrop}
          onConfirm={confirmSidebarCrop}
          isUploading={sidebarPhotoUploading}
        />
      </SidebarFooter>
      <HelpChatWidget open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Sidebar>
  );
}
